import React, { useMemo, useState } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import {
  yearTotal,
  yearToDateTotal,
} from '../lib/ledger'
import MultiSelect from '../components/MultiSelect'
import Select from '../components/Select'
import LoadingSpinner from '../components/LoadingSpinner'
import ReportsTabs from '../components/ReportsTabs'
import type { Project, Settings } from '../types'

const fmt = (v: number) => {
  const r = Math.round(v)
  return r.toLocaleString()
}

// Cell background: green tint for positive values.
const cellBg = (value: number) => value > 0 ? 'bg-[#00843D]/10' : ''

type ProjectRow = {
  id: string
  name: string
  description: string
  priorActual: number   // e.g. 2025 actual
  ytdActual: number     // 2026 YTD
  cumulative: number    // priorActual + ytdActual
  curTarget: number     // 2026 full-year target
  nextTarget: number    // 2027 target
}

type TeamGroup = {
  team: string
  projects: ProjectRow[]
  priorActual: number
  ytdActual: number
  cumulative: number
  curTarget: number
  nextTarget: number
}

export default function Scorecard() {
  useDocumentTitle('Scorecard')
  const api = useApi()
  const { data, loading } = useData(() =>
    Promise.all([
      api.getProjects(),
      api.getSettings(),
    ]).then(([projects, settings]) => ({ projects, settings })),
  )

  const typed = data as { projects: Project[]; settings: Settings } | undefined
  const projects = typed?.projects ?? []
  const settings = typed?.settings

  const now = new Date()
  const curYear = now.getFullYear()
  const priorYear = curYear - 1
  const curMonth = now.getMonth() + 1

  const configuredTeams = useMemo(() => settings?.teams ?? [], [settings])

  const [teamFilter, setTeamFilter] = useState<string[]>([])
  const [yearFilter, setYearFilter] = useState<string>('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Year filter = year project was delivered (actualGoLive) for complete,
  // or expected delivery year (dueDate) for forecast.
  const yearOf = (p: Project) =>
    p.status === 'complete'
      ? p.actualGoLive ? new Date(p.actualGoLive).getFullYear().toString() : null
      : p.dueDate ? new Date(p.dueDate).getFullYear().toString() : null
  const availableYears = useMemo(
    () => [...new Set(projects.map(yearOf).filter(Boolean) as string[])].sort().reverse(),
    [projects],
  )

  const toggleTeam = (team: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(team)) next.delete(team)
      else next.add(team)
      return next
    })
  }

  const expandAll = () => setExpanded(new Set(groups.map(g => g.team)))
  const collapseAll = () => setExpanded(new Set())

  // ── Value Scorecard (Hours Saved) ──────────────────────────────────────────
  const { groups, grandTotals } = useMemo(() => {
    // Include all projects that are complete or forecast, narrowed by the
    // year filter. Complete = actualGoLive year, forecast = dueDate year.
    const relevant = projects
      .filter(p => p.status === 'complete' || p.status === 'forecast')
      .filter(p => !yearFilter || yearOf(p) === yearFilter)

    const teamMap = new Map<string, ProjectRow[]>()
    const ensureTeam = (t: string) => {
      if (!teamMap.has(t)) teamMap.set(t, [])
      return teamMap.get(t)!
    }

    // Prorate a forecast project's annual expectedValue into a calendar year,
    // matching the backend ledger seed (projects.py _build_seed_plan): the
    // go-live year is prorated from the go-live month through December
    // (rate = ev/12); a full year after go-live gets the whole ev; a year
    // before go-live gets nothing. Keeps forecast targets consistent with what
    // the ledger will actually show once the project completes.
    const proratedTarget = (ev: number, goLiveYear: number, goLiveMonth: number, year: number): number => {
      if (goLiveYear > year) return 0
      if (goLiveYear < year) return ev
      return (13 - goLiveMonth) * (ev / 12) // go-live month → Dec
    }

    for (const p of relevant) {
      const team = p.team || 'Unassigned'
      const ledger = p.monthlyLedger
      const goLiveYear = p.actualGoLive ? new Date(p.actualGoLive).getFullYear() : null

      let priorActual = 0
      let ytdActual = 0
      let curTarget = 0
      let nextTarget = 0

      if (p.status === 'complete' && ledger) {
        priorActual = yearTotal(ledger, priorYear)
        ytdActual = yearToDateTotal(ledger, curYear, curMonth)
        curTarget = yearTotal(ledger, curYear)
        nextTarget = yearTotal(ledger, curYear + 1)
      }

      if (p.status === 'forecast') {
        const ev = p.expectedValue || 0
        if (ev === 0) continue
        // Use dueDate to approximate go-live year/month for forecast projects
        // (actualGoLive is always null while still in forecast status).
        const refDate = p.dueDate ? new Date(p.dueDate) : null
        if (refDate) {
          const gY = refDate.getFullYear()
          const gM = refDate.getMonth() + 1 // 1-12
          curTarget = proratedTarget(ev, gY, gM, curYear)
          nextTarget = proratedTarget(ev, gY, gM, curYear + 1)
        } else {
          // No due date to anchor the go-live - book the full value to the
          // current year rather than dropping the project.
          curTarget = ev
        }
      }

      // Skip projects where all values are zero
      if (priorActual === 0 && ytdActual === 0 && curTarget === 0 && nextTarget === 0) continue

      const cumulative = priorActual + ytdActual

      ensureTeam(team).push({
        id: p.id,
        name: p.name,
        description: p.description || '',
        priorActual,
        ytdActual,
        cumulative,
        curTarget,
        nextTarget,
      })
    }

    const grps: TeamGroup[] = [...teamMap.entries()]
      .map(([team, projs]) => {
        const sum = (fn: (p: ProjectRow) => number) => projs.reduce((s, p) => s + fn(p), 0)
        return {
          team,
          projects: projs,
          priorActual: sum(p => p.priorActual),
          ytdActual: sum(p => p.ytdActual),
          cumulative: sum(p => p.cumulative),
          curTarget: sum(p => p.curTarget),
          nextTarget: sum(p => p.nextTarget),
        }
      })
      .filter(g => g.projects.length > 0)
      .sort((a, b) => a.team.localeCompare(b.team))

    const gt = grps.reduce(
      (acc, g) => ({
        priorActual: acc.priorActual + g.priorActual,
        ytdActual: acc.ytdActual + g.ytdActual,
        cumulative: acc.cumulative + g.cumulative,
        curTarget: acc.curTarget + g.curTarget,
        nextTarget: acc.nextTarget + g.nextTarget,
      }),
      { priorActual: 0, ytdActual: 0, cumulative: 0, curTarget: 0, nextTarget: 0 },
    )

    return { groups: grps, grandTotals: gt }
  }, [projects, configuredTeams, priorYear, curYear, curMonth, yearFilter])

  if (loading || !data || !settings) return <LoadingSpinner />

  const filteredGroups = teamFilter.length > 0
    ? groups.filter(g => teamFilter.includes(g.team))
    : groups

  const filteredTotals = teamFilter.length > 0
    ? filteredGroups.reduce(
        (acc, g) => ({
          priorActual: acc.priorActual + g.priorActual,
          ytdActual: acc.ytdActual + g.ytdActual,
          cumulative: acc.cumulative + g.cumulative,
          curTarget: acc.curTarget + g.curTarget,
          nextTarget: acc.nextTarget + g.nextTarget,
        }),
        { priorActual: 0, ytdActual: 0, cumulative: 0, curTarget: 0, nextTarget: 0 },
      )
    : grandTotals

  const allTeams = groups.map(g => g.team)

  const ytdLabel = `${curYear} Jan-${new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(now)} Actual`

  return (
    <div>
      <ReportsTabs active="scorecard" />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Cumulative Scorecard</h1>
        <div className="flex items-center gap-3">
          {availableYears.length > 0 && (
            <Select value={yearFilter} onChange={setYearFilter} options={availableYears} placeholder="All Years" />
          )}
          <MultiSelect values={teamFilter} onChange={setTeamFilter} options={allTeams} placeholder="Filter by team" />
          <button onClick={expanded.size > 0 ? collapseAll : expandAll} className="text-xs text-danone-cyan font-semibold hover:text-danone-blue">
            {expanded.size > 0 ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#005EB8] text-white">
                <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wide border-r border-blue-700 w-36">Team</th>
                <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wide border-r border-blue-700">Initiative</th>
                <th className="px-4 py-3 text-right font-bold text-xs uppercase tracking-wide border-r border-blue-700 whitespace-nowrap w-24">{priorYear} Actual<br /><span className="font-normal text-blue-200">(hrs)</span></th>
                <th className="px-4 py-3 text-right font-bold text-xs uppercase tracking-wide border-r border-blue-700 whitespace-nowrap w-24">{ytdLabel}<br /><span className="font-normal text-blue-200">(hrs)</span></th>
                <th className="px-4 py-3 text-right font-bold text-xs uppercase tracking-wide border-r border-blue-700 whitespace-nowrap w-28">{priorYear}-{curYear}<br />Actuals<br /><span className="font-normal text-blue-200">(hrs)</span></th>
                <th className="px-4 py-3 text-right font-bold text-xs uppercase tracking-wide border-r border-blue-700 whitespace-nowrap w-24">{curYear} Target</th>
                <th className="px-4 py-3 text-right font-bold text-xs uppercase tracking-wide whitespace-nowrap w-24">{curYear + 1} Target</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-400">No project data yet.</td></tr>
              ) : (
                <>
                  {filteredGroups.map(group => {
                    const isExpanded = expanded.has(group.team)
                    return (
                      <React.Fragment key={group.team}>
                        {/* Team summary row (clickable) */}
                        <tr
                          className="border-t-2 border-t-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100"
                          onClick={() => toggleTeam(group.team)}
                        >
                          <td className="px-4 py-3 font-bold text-slate-800 uppercase text-xs tracking-wide border-r border-slate-200">
                            <span className="flex items-center gap-1.5">
                              {isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                              {group.team}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-r border-slate-100 text-xs text-slate-500">{group.projects.length} initiative{group.projects.length !== 1 ? 's' : ''}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-semibold border-r border-slate-100 ${cellBg(group.priorActual)}`}>{fmt(group.priorActual)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-semibold border-r border-slate-100 ${cellBg(group.ytdActual)}`}>{fmt(group.ytdActual)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-bold border-r border-slate-100 ${cellBg(group.cumulative)}`}>{fmt(group.cumulative)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-semibold border-r border-slate-100 ${cellBg(group.curTarget)}`}>{fmt(group.curTarget)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-semibold ${cellBg(group.nextTarget)}`}>{fmt(group.nextTarget)}</td>
                        </tr>
                        {/* Individual project rows (only when expanded) */}
                        {isExpanded && group.projects.map(p => (
                          <tr key={p.id} className="border-b border-slate-100">
                            <td className="border-r border-slate-200" />
                            <td className="px-4 py-3 border-r border-slate-100">
                              <div className="font-semibold text-slate-800 text-xs uppercase">{p.name}</div>
                              {p.description && (
                                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{p.description}</div>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums border-r border-slate-100 ${cellBg(p.priorActual)}`}>
                              {p.priorActual > 0 ? fmt(p.priorActual) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums border-r border-slate-100 ${cellBg(p.ytdActual)}`}>
                              {p.ytdActual > 0 ? fmt(p.ytdActual) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums font-semibold border-r border-slate-100 ${cellBg(p.cumulative)}`}>
                              {p.cumulative > 0 ? fmt(p.cumulative) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums border-r border-slate-100 ${cellBg(p.curTarget)}`}>
                              {p.curTarget > 0 ? fmt(p.curTarget) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums ${cellBg(p.nextTarget)}`}>
                              {p.nextTarget > 0 ? fmt(p.nextTarget) : <span className="text-slate-300">-</span>}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    )
                  })}
                  {/* Grand total row */}
                  <tr className="bg-[#005EB8] text-white font-bold border-t-2 border-slate-300">
                    <td className="px-4 py-3 uppercase text-xs tracking-wide" colSpan={2}>Total</td>
                    <td className="px-4 py-3 text-right tabular-nums border-r border-blue-700">{fmt(filteredTotals.priorActual)}</td>
                    <td className="px-4 py-3 text-right tabular-nums border-r border-blue-700">{fmt(filteredTotals.ytdActual)}</td>
                    <td className="px-4 py-3 text-right tabular-nums border-r border-blue-700">{fmt(filteredTotals.cumulative)}</td>
                    <td className="px-4 py-3 text-right tabular-nums border-r border-blue-700">{fmt(filteredTotals.curTarget)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(filteredTotals.nextTarget)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  )
}
